import React, { useState, useMemo, useCallback } from "react"
import { Modal, Input, Checkbox } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { SearchOutlined } from "@ant-design/icons"
import { useExportMapStore } from "stores/exportMapStore"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

interface ExportMapCategorySetModalProps {
  open: boolean
  onClose: () => void
}

interface MatchResult {
  variableLabel: string
  variableName: string
  categoryLabel: string
  currentCode: number | string
}

/**
 * Modal for finding categories by text pattern and setting their codes.
 * Useful for bulk setting codes for common categories like "Don't know" or "Refused".
 */
const ExportMapCategorySetModal: React.FC<ExportMapCategorySetModalProps> = ({
  open,
  onClose,
}) => {
  const { allVariables, bulkSetCategoryCode } = useExportMapStore()

  const [searchPattern, setSearchPattern] = useState("")
  const [newCode, setNewCode] = useState("")
  const [caseInsensitive, setCaseInsensitive] = useState(true)
  const [exactMatch, setExactMatch] = useState(false)

  // Find matching categories
  const matchingCategories = useMemo((): MatchResult[] => {
    if (!searchPattern.trim()) return []

    const results: MatchResult[] = []
    const pattern = caseInsensitive
      ? searchPattern.toLowerCase()
      : searchPattern

    for (const variable of allVariables) {
      if (!variable.categories) continue

      for (const category of variable.categories) {
        const label = caseInsensitive
          ? category.label.toLowerCase()
          : category.label

        const matches = exactMatch
          ? label === pattern
          : label.includes(pattern)

        if (matches) {
          results.push({
            variableLabel: variable.fullLabel,
            variableName: variable.path[variable.path.length - 1] || variable.fullLabel,
            categoryLabel: category.label,
            currentCode: category.code,
          })
        }
      }
    }

    return results
  }, [searchPattern, caseInsensitive, exactMatch, allVariables])

  // Get unique variable count
  const uniqueVariableCount = useMemo(() => {
    const uniqueVars = new Set(matchingCategories.map((m) => m.variableLabel))
    return uniqueVars.size
  }, [matchingCategories])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchPattern(e.target.value)
    },
    []
  )

  const handleCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewCode(e.target.value)
    },
    []
  )

  const handleApply = () => {
    if (!searchPattern.trim()) {
      notify.toast.warning("Please enter a search pattern")
      return
    }

    if (!newCode.trim()) {
      notify.toast.warning("Please enter a code value")
      return
    }

    // Parse the code - try as number first, then string
    const parsedCode = /^\d+$/.test(newCode.trim())
      ? parseInt(newCode.trim(), 10)
      : newCode.trim()

    const { variableCount, categoryCount } = bulkSetCategoryCode(
      searchPattern,
      parsedCode,
      caseInsensitive,
      exactMatch
    )

    if (categoryCount > 0) {
      notify.toast.success(
        `Updated ${categoryCount} categories across ${variableCount} variables`
      )
    } else {
      notify.toast.info("No matching categories found")
    }

    // Reset and close
    setSearchPattern("")
    setNewCode("")
    onClose()
  }

  const handleClose = () => {
    setSearchPattern("")
    setNewCode("")
    setCaseInsensitive(true)
    setExactMatch(false)
    onClose()
  }

  const isApplyDisabled = !searchPattern.trim() || !newCode.trim() || matchingCategories.length === 0

  return (
    <Modal
      title="Set Category Codes"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={550}
    >
      <div className="space-y-4">
        {/* Search Pattern */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Find categories containing
          </label>
          <Input
            placeholder="e.g., Don't know"
            value={searchPattern}
            onChange={handleSearchChange}
            prefix={<SearchOutlined className="text-gray-400" />}
            allowClear
          />
        </div>

        {/* New Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Set code to
          </label>
          <Input
            placeholder="e.g., 996"
            value={newCode}
            onChange={handleCodeChange}
            maxLength={20}
            className="w-40"
          />
        </div>

        {/* Match Options */}
        <div className="flex gap-4">
          <Checkbox
            checked={caseInsensitive}
            onChange={(e) => setCaseInsensitive(e.target.checked)}
          >
            Case insensitive
          </Checkbox>
          <Checkbox
            checked={exactMatch}
            onChange={(e) => setExactMatch(e.target.checked)}
          >
            Exact match only
          </Checkbox>
        </div>

        {/* Match Results */}
        {searchPattern.trim() && (
          <div className="bg-gray-50 p-3 rounded">
            {matchingCategories.length > 0 ? (
              <>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Found {matchingCategories.length} matching{" "}
                  {matchingCategories.length === 1 ? "category" : "categories"} across{" "}
                  {uniqueVariableCount}{" "}
                  {uniqueVariableCount === 1 ? "variable" : "variables"}
                </div>
                <div className="max-h-40 overflow-auto space-y-1">
                  {matchingCategories.slice(0, 10).map((match, i) => (
                    <div
                      key={`${match.variableLabel}-${match.categoryLabel}-${i}`}
                      className="text-xs flex items-center gap-2"
                    >
                      <span className="text-gray-500 truncate max-w-[150px]">
                        {match.variableName}
                      </span>
                      <span className="text-gray-300">→</span>
                      <span className="font-medium truncate max-w-[150px]">
                        {match.categoryLabel}
                      </span>
                      <span className="text-gray-400">
                        (currently: {match.currentCode})
                      </span>
                    </div>
                  ))}
                  {matchingCategories.length > 10 && (
                    <div className="text-xs text-gray-400 mt-1">
                      ...and {matchingCategories.length - 10} more
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500">
                No matching categories found
              </div>
            )}
          </div>
        )}

        {/* Info text */}
        <div className="text-xs text-gray-500">
          {exactMatch
            ? "This will find categories with labels that exactly match the search text."
            : "This will find categories whose labels contain the search text."}
          {" "}Useful for standardizing codes for common responses like "Don't know" (996) or "Refused" (999).
        </div>

        {/* Footer buttons */}
        <div className="flex flex-row space-x-2 pt-2">
          <DataQButton className="ml-auto" type="primary" onClick={handleClose}>
            Cancel
          </DataQButton>
          <DataQButton
            type="secondary"
            onClick={handleApply}
            isDisabled={isApplyDisabled}
          >
            Apply
          </DataQButton>
        </div>
      </div>
    </Modal>
  )
}

export default ExportMapCategorySetModal
